<template>
  <div class="home-swiper">
    <!-- 轮播容器 - 设置overflow隐藏 -->
    <div class="swiper-container">
      <!-- 轮播轨道 - 用于移动整个轮播项 -->
      <div class="swiper-track" :style="{
        transform: `translateX(-${currentPosition * 100}%)`,
        transition: transitionDuration
      }">
        <!-- 轮播项 - 始终显示当前和下一个元素 -->
        <div class="swiper-slide" v-for="(item, index) in displayList" :key="index">
          <!-- 视频背景 -->
          <div class="slide-video">
            <video src="../store/new/goods.mp4" class="s-video" autoplay muted loop playsinline></video>
          </div>

          <!-- 轮播内容图片 -->
          <div class="slide-content">
            <img src="https://youke1.picui.cn/s1/2025/08/27/68af099069888.png" v-if="item.type == '2'"
              class="content-type"></img>
            <img src="https://youke1.picui.cn/s1/2025/08/27/68af09a2705b2.png" v-if="item.type == '1'"
              class="content-type"></img>
            <div class="slid-title">
              {{ item.title }}
            </div>
            <div class="slid-tips">
              {{ item.tips }}
            </div>
            <img src="https://youke1.picui.cn/s1/2025/08/27/68af09d5837bb.png" class="content-img"></img>
            <div class="goods-img-box">
              <img :src="item.goodsImg" class="goods-img"></img>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 左切换按钮 -->
    <img src="https://youke1.picui.cn/s1/2025/08/27/68af0900e0b9e.png" class="control-btn left-btn" alt="上一页"
      @click="prevSlide" :disabled="isProcessing">

    <!-- 右切换按钮 -->
    <img src="https://youke1.picui.cn/s1/2025/08/27/68af0900d4f3f.png" class="control-btn right-btn" alt="下一页"
      @click="nextSlide" :disabled="isProcessing">

    <!-- 指示器 -->
    <div class="swiper-indicators">
      <span class="indicator" v-for="(item, index) in originalList" :key="index"
        :class="{ active: currentIndex === index }" @click="goToSlide(index)"></span>
    </div>

  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, defineProps, watch, nextTick } from 'vue'

// 接收父组件传递的goodList
const props = defineProps({
  goodList: {
    type: Array,
    default: () => [],
    required: true
  }
})

// 原始数组（源数据）
const originalList = ref([...props.goodList])
// 用于显示的动态数组
const displayList = ref([])
// 当前在原始数组中的索引（用于同步指示器）
const currentIndex = ref(0)
// 轮播位置（0或1，用于触发滑动动画）
const currentPosition = ref(0)
// 过渡动画控制
const transitionDuration = ref('transform 0.5s ease-out')
// 防止快速连续点击
const isProcessing = ref(false)
// 自动播放定时器
let timer = null

// 初始化显示数组
const initDisplayList = () => {
  if (originalList.value.length > 0) {
    // 初始显示当前项和下一项
    displayList.value = [
      originalList.value[currentIndex.value],
      originalList.value[(currentIndex.value + 1) % originalList.value.length]
    ]
  }
}

// 右滑逻辑：将第一个元素移到最后
const nextSlide = () => {
  if (isProcessing.value || originalList.value.length <= 1) return
  isProcessing.value = true

  // 1. 触发右滑动画
  transitionDuration.value = 'transform 0.5s ease-out'
  currentPosition.value = 1

  // 2. 动画结束后调整数组
  setTimeout(() => {
    // 更新原始数组索引
    currentIndex.value = (currentIndex.value + 1) % originalList.value.length

    // 3. 调整显示数组（删除第一个，添加新的下一项）
    displayList.value.shift() // 删除第一个元素
    displayList.value.push(originalList.value[(currentIndex.value + 1) % originalList.value.length])

    // 4. 重置位置（无动画）
    transitionDuration.value = 'none'
    currentPosition.value = 0

    isProcessing.value = false
  }, 500)
}

// 左滑逻辑：将最后一个元素移到最前
const prevSlide = () => {
  if (isProcessing.value || originalList.value.length <= 1) return
  isProcessing.value = true

  // 1. 先调整数组（无动画）
  transitionDuration.value = 'none'
  // 更新原始数组索引
  currentIndex.value = (currentIndex.value - 1 + originalList.value.length) % originalList.value.length
  // 在数组开头添加新的前一项
  displayList.value.unshift(originalList.value[currentIndex.value])
  // 触发左滑动画
  currentPosition.value = 1

  // 2. 强制DOM更新后启用动画
  setTimeout(() => {
    transitionDuration.value = 'transform 0.5s ease-out'
    currentPosition.value = 0

    // 3. 动画结束后清理数组
    setTimeout(() => {
      displayList.value.pop() // 删除最后一个元素
      isProcessing.value = false
    }, 500)
  }, 10)
}

// 跳转到指定页
const goToSlide = (index) => {
  if (isProcessing.value || index === currentIndex.value) return

  // 计算需要右滑还是左滑更优
  const diff = index - currentIndex.value
  const len = originalList.value.length

  if (diff > 0 || (diff < 0 && Math.abs(diff) > len / 2)) {
    // 右滑更优
    const steps = diff > 0 ? diff : (diff + len)
    for (let i = 0; i < steps; i++) {
      // 同步更新索引
      currentIndex.value = (currentIndex.value + 1) % len
    }
    nextSlide()
  } else {
    // 左滑更优
    const steps = diff < 0 ? Math.abs(diff) : (len - diff)
    for (let i = 0; i < steps; i++) {
      // 同步更新索引
      currentIndex.value = (currentIndex.value - 1 + len) % len
    }
    prevSlide()
  }
}

// 自动播放
const startAutoPlay = () => {
  timer = setInterval(() => {
    nextSlide()
  }, 5000) // 5秒切换一次
}

// 暂停自动播放
const stopAutoPlay = () => {
  if (timer) {
    clearInterval(timer)
    timer = null
  }
}

// 监听原始数据变化，重新初始化
watch(() => props.goodList, (newVal) => {
  originalList.value = [...newVal]
  currentIndex.value = 0
  initDisplayList()
})

// 组件挂载时初始化
onMounted(() => {
  initDisplayList()
  if (originalList.value.length > 1) {
    startAutoPlay()
  }

  // 鼠标悬停暂停自动播放
  const container = document.querySelector('.home-swiper')
  container.addEventListener('mouseenter', stopAutoPlay)
  container.addEventListener('mouseleave', startAutoPlay)
})

// 组件卸载时清除定时器
onUnmounted(() => {
  stopAutoPlay()
  const container = document.querySelector('.home-swiper')
  container.removeEventListener('mouseenter', stopAutoPlay)
  container.removeEventListener('mouseleave', startAutoPlay)
})
</script>

<style scoped>
.home-swiper {
  background-color: #F4ECE1;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.swiper-container {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  /* 隐藏超出容器的内容 */
}

.swiper-track {
  display: flex;
  /* 让轮播项横向排列 */
  height: 100%;
  /* 滑动过渡动画由JS控制 */
}

.swiper-slide {
  min-width: 100%;
  /* 每个轮播项占满容器宽度 */
  height: 100%;
  position: relative;
}

.slide-video {
  position: absolute;
  left: 30%;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
  height: auto;
  z-index: 1;
}

.slide-video video {
  height: 100%;
  object-fit: cover;
}

.slide-content {
  z-index: 99999999;
  position: absolute;
  left: 10%;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;

}

.slid-title {
  position: absolute;
  color: #6A584E;
  font-size: 22px;
  width: 100%;
  text-align: center;
  top: 248px;
}

.slid-tips {
  position: absolute;
  color: #A58065;
  font-size: 22px;
  width: 100%;
  text-align: center;
  top: 288px;
}

.content-img {
  height: 600px;
}

.goods-img-box {
  position: absolute;
  top: 350px;
  width: 100%;
  display: flex;
  justify-content: center;
}

.goods-img {
  width: 280px;
  height: 100%;
}

.content-type {
  height: 120px;
  width: 120px;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 20px;
}

.control-btn {
  width: 40px;
  height: 40px;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  z-index: 100;
  transition: transform 0.3s ease;
}

.control-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.control-btn:hover:not(:disabled) {
  transform: translateY(-50%) scale(1.1);
}

.left-btn {
  left: 50px;
}

.right-btn {
  right: 50px;
}

.swiper-indicators {
  position: absolute;
  bottom: 80px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 12px;
  z-index: 100;
}

.swiper-indicators span {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: rgba(106, 88, 78, 0.5);
  cursor: pointer;
  transition: all 0.3s ease;
}

.swiper-indicators span.active {
  background-color: #6A584E;
  width: 30px;
  border-radius: 6px;
}

.s-video {
  width: 65vw;
  height: 100%;
  object-fit: cover;
  object-position: center;
}
</style>
